///
/// EarthBrowser
///
/// Created by Matt Giger on 10/29/2026
/// Copyright (c) 2026 Geodata Labs
///
/// The one hover-card template. Every entity on the globe describes its
/// preview as a HoverCardSpec (title, optional hero figure, badges, a few
/// labelled fields, footer, optional image) and this canvas lays it out
/// with a single grammar that mirrors the focused React panels
/// (AnchoredPanelShell / panelAtoms): the same 3x44 accent pill, the same
/// dark glass, sentence-case slate labels, a light hero numeral, pill
/// badges and a monospace footer. Colour lives in the pill, border, hero
/// and badges only, so a dataset's accent reads as identity rather than
/// tinting every word.
///
/// Canvas can't blur the backdrop the way the React cards do, so the
/// glass is darker (0.76) to keep terrain and city labels from showing
/// through 12px text.
///

import { Canvas } from "@stovokor/uipanes/Canvas"

// ============================================================================
// Spec
// ============================================================================

export interface HoverCardBadge {
	text: string
	/** CSS colour (hex or rgb()); drawn at 18% as fill and full as text */
	color: string
}

export interface HoverCardField {
	/** Sentence-case label ("Depth", "Wind"); abbreviations are not the house style */
	label: string
	value: string
	/** Tabular monospace value (coordinates, ids); numbers in prose stay proportional */
	mono?: boolean
	/** Label colour override, for cross-dataset rows (nearby webcam, sea, aurora) */
	labelColor?: string
	/** Take the whole row instead of one of the two columns */
	wide?: boolean
}

export interface HoverCardHero {
	/** The one figure the card exists to deliver ("M 2.3", "73°", "6,788 acres") */
	value: string
	caption?: string
	/** Colour for the figure; defaults to white */
	color?: string
}

export interface HoverCardMedia {
	url: string
	/** width / height */
	aspect: number
	/** Badges drawn over the image's top-left corner */
	badges?: HoverCardBadge[]
	/** Show "Nm ago" in the bottom-right from the image's upload timestamp */
	showCapturedAge?: boolean
}

export interface HoverCardSpec {
	/** Dataset accent (pill, border, connector) */
	accent: string
	title: string
	subtitle?: string
	badges?: HoverCardBadge[]
	hero?: HoverCardHero
	media?: HoverCardMedia
	/** At most six are drawn: a hover card is a preview, the panel has the rest */
	fields?: HoverCardField[]
	footer?: { left?: string; right?: string }
}

/** Standard card width; every card is the same width so a hover never jumps size */
export const cHoverCardWidth = 264

// ============================================================================
// Visual tokens (CSS-string fonts match the React chrome: Inter + mono)
// ============================================================================

const cSans = "Inter, system-ui, -apple-system, sans-serif"
const cMono = "ui-monospace, 'JetBrains Mono', Menlo, Consolas, monospace"

const cFont = {
	title: `600 14px ${cSans}`,
	subtitle: `400 12px ${cSans}`,
	badge: `600 10px ${cSans}`,
	hero: `300 24px ${cSans}`,
	heroCaption: `400 12px ${cSans}`,
	label: `400 11.5px ${cSans}`,
	value: `500 12px ${cSans}`,
	valueMono: `500 11.5px ${cMono}`,
	footer: `400 10px ${cMono}`,
	placeholder: `400 12px ${cSans}`,
}

const cColor = {
	// Opaque slate-950. Canvas can't blur what's behind it, and any
	// translucency here composites lighter than linear over bright pixels
	// (a 0.94 fill still ghosted white city labels at ~50% - measured),
	// so the card is solid; the React cards keep their blurred glass
	glass: "rgb(2, 6, 23)",
	hairline: "rgba(255, 255, 255, 0.08)",
	title: "rgba(255, 255, 255, 0.96)",
	subtitle: "rgba(148, 163, 184, 0.95)",   // slate-400
	label: "rgba(148, 163, 184, 0.85)",      // slate-400
	value: "rgba(226, 232, 240, 0.96)",      // slate-200
	hero: "rgba(255, 255, 255, 0.96)",
	heroCaption: "rgba(203, 213, 225, 0.85)", // slate-300
	footer: "rgba(148, 163, 184, 0.75)",
	mediaBackdrop: "rgba(15, 23, 42, 0.6)",
	overlayBadge: "rgba(15, 23, 42, 0.72)",
}

const cPad = 14
const cRadius = 12
const cMaxFields = 6
const cLineTitle = 17
const cRowField = 17
const cBadgeHeight = 16

// ============================================================================
// Canvas
// ============================================================================

type Ctx2D = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D

/** Rows of the laid-out card, computed once per spec for measure + render */
interface Layout {
	height: number
	titleLines: string[]
}

// The card is measured before its render surface exists (placement needs
// the height first), so text metrics come from a shared 1x1 scratch context
let scratchContext: OffscreenCanvasRenderingContext2D | undefined
function measuringContext(): OffscreenCanvasRenderingContext2D {
	if (!scratchContext) {
		scratchContext = new OffscreenCanvas(1, 1).getContext("2d") ?? undefined
		if (!scratchContext) throw new Error("HoverCard: no 2d context")
	}
	return scratchContext
}

export class HoverCardCanvas extends Canvas {
	private spec?: HoverCardSpec
	private layout?: Layout

	// Image cache for media cards: the fetch resolves async, then
	// setNeedsUpdate triggers a re-render that draws the bitmap
	private imageRequestedUrl?: string
	private imageBitmap?: ImageBitmap
	private imageBitmapUrl?: string
	private imageFailed = false
	// When the CDN image was uploaded (x-bz-upload-timestamp, epoch ms):
	// the ingest uploads at capture time, so this is when the preview was
	// taken. Last-Modified is synthesised by the CDN and useless here.
	private imageCapturedMs: number | null = null

	public setSpec(spec: HoverCardSpec | undefined): void {
		this.spec = spec
		this.layout = undefined
		this.setNeedsUpdate()
	}

	public getSpec(): HoverCardSpec | undefined {
		return this.spec
	}

	/** Height the current spec needs at the standard width */
	public measuredHeight(): number {
		return this.ensureLayout().height
	}

	// ---------------------------------------------------------------- layout

	private ensureLayout(): Layout {
		if (this.layout) return this.layout
		const spec = this.spec
		if (!spec) {
			this.layout = { height: 0, titleLines: [] }
			return this.layout
		}
		const ctx = measuringContext()
		const contentWidth = cHoverCardWidth - cPad * 2

		ctx.font = cFont.title
		const titleLines = wrapText(ctx, spec.title, contentWidth, 2)

		let y = 12 + 3 + 8                       // pill + gap
		y += titleLines.length * cLineTitle      // title block (baselines at y - 4)
		if (spec.subtitle) y += 15
		if (spec.badges && spec.badges.length > 0) y += cBadgeHeight + 7
		y += 8                                   // header → hairline
		let body = 0
		if (spec.hero) body += 34
		if (spec.media) body += Math.round((contentWidth) / spec.media.aspect) + 8
		const fields = (spec.fields ?? []).slice(0, cMaxFields)
		if (fields.length > 0) body += fieldRows(fields) * cRowField + 4
		if (body > 0) y += 8 + body
		if (spec.footer && (spec.footer.left || spec.footer.right)) y += 20
		y += 10

		this.layout = { height: Math.round(y), titleLines }
		return this.layout
	}

	// ---------------------------------------------------------------- render

	override render(): void {
		this.clear()
		const spec = this.spec
		if (!spec) return
		const layout = this.ensureLayout()
		const ctx = this.context
		const w = this.width
		const h = this.height
		const contentWidth = cHoverCardWidth - cPad * 2

		// Shell: dark glass, accent hairline border
		ctx.beginPath()
		ctx.roundRect(0.5, 0.5, w - 1, h - 1, cRadius)
		ctx.fillStyle = cColor.glass
		ctx.fill()
		ctx.strokeStyle = withAlpha(spec.accent, 0.4)
		ctx.lineWidth = 1
		ctx.stroke()

		ctx.textBaseline = "alphabetic"
		ctx.textAlign = "left"

		// Accent pill (3x44, the same mark the React panels open with)
		ctx.fillStyle = spec.accent
		ctx.beginPath()
		ctx.roundRect(cPad, 12, 44, 3, 1.5)
		ctx.fill()

		// Title (wraps to two lines)
		let y = 12 + 3 + 8 + cLineTitle - 4
		ctx.font = cFont.title
		ctx.fillStyle = cColor.title
		for (const line of layout.titleLines) {
			ctx.fillText(line, cPad, y)
			y += cLineTitle
		}
		y -= cLineTitle

		if (spec.subtitle) {
			y += 15
			ctx.font = cFont.subtitle
			ctx.fillStyle = cColor.subtitle
			ctx.fillText(clipText(ctx, spec.subtitle, contentWidth, cFont.subtitle), cPad, y)
		}

		if (spec.badges && spec.badges.length > 0) {
			y += 7
			drawBadgeRow(ctx, spec.badges, cPad, y, contentWidth)
			y += cBadgeHeight
		}

		// Header hairline
		y += 8
		ctx.fillStyle = cColor.hairline
		ctx.fillRect(cPad, y, contentWidth, 1)

		let bodyTop = y + 8

		if (spec.hero) {
			const baseline = bodyTop + 24
			ctx.font = cFont.hero
			ctx.fillStyle = spec.hero.color ?? cColor.hero
			ctx.fillText(spec.hero.value, cPad, baseline)
			if (spec.hero.caption) {
				const valueWidth = ctx.measureText(spec.hero.value).width
				const captionX = cPad + valueWidth + 8
				ctx.font = cFont.heroCaption
				ctx.fillStyle = cColor.heroCaption
				ctx.fillText(clipText(ctx, spec.hero.caption, cHoverCardWidth - captionX - cPad, cFont.heroCaption), captionX, baseline - 1)
			}
			bodyTop += 34
		}

		if (spec.media) {
			const imgH = Math.round(contentWidth / spec.media.aspect)
			this.drawMedia(ctx, spec.media, cPad, bodyTop, contentWidth, imgH)
			bodyTop += imgH + 8
		}

		const fields = (spec.fields ?? []).slice(0, cMaxFields)
		if (fields.length > 0) {
			const col2 = cPad + contentWidth / 2 + 4
			let fy = bodyTop + 12
			let col = 0
			for (const field of fields) {
				const wide = field.wide === true
				if (wide && col === 1) {
					fy += cRowField
					col = 0
				}
				const x = col === 0 ? cPad : col2
				const maxW = wide ? contentWidth : (col === 0 ? col2 - cPad - 8 : cHoverCardWidth - col2 - cPad)
				drawField(ctx, field, x, fy, maxW)
				if (wide) {
					fy += cRowField
					col = 0
				} else if (col === 0) {
					col = 1
				} else {
					col = 0
					fy += cRowField
				}
			}
		}

		// Footer is anchored to the bottom edge so the measured height and
		// the drawn card can't disagree by a rounding error
		if (spec.footer && (spec.footer.left || spec.footer.right)) {
			const footerY = h - 10 - 4
			ctx.fillStyle = withAlpha("#ffffff", 0.06)
			ctx.fillRect(cPad, footerY - 13, contentWidth, 1)
			ctx.font = cFont.footer
			ctx.fillStyle = cColor.footer
			let rightWidth = 0
			if (spec.footer.right) {
				rightWidth = ctx.measureText(spec.footer.right).width
				ctx.fillText(spec.footer.right, cHoverCardWidth - cPad - rightWidth, footerY)
			}
			if (spec.footer.left) {
				const maxLeft = contentWidth - (rightWidth > 0 ? rightWidth + 10 : 0)
				ctx.fillText(clipText(ctx, spec.footer.left, maxLeft, cFont.footer), cPad, footerY)
			}
		}
	}

	// ---------------------------------------------------------------- media

	private drawMedia(ctx: Ctx2D, media: HoverCardMedia, x: number, y: number, w: number, h: number): void {
		this.ensureImage(media.url)

		ctx.save()
		ctx.beginPath()
		ctx.roundRect(x, y, w, h, 8)
		ctx.clip()
		if (this.imageBitmap && this.imageBitmapUrl === media.url) {
			// Cover-fit: scale to fill the frame and center-crop the overflow
			const bmp = this.imageBitmap
			const scale = Math.max(w / bmp.width, h / bmp.height)
			const cropW = w / scale
			const cropH = h / scale
			ctx.drawImage(bmp, (bmp.width - cropW) / 2, (bmp.height - cropH) / 2, cropW, cropH, x, y, w, h)
		} else {
			ctx.fillStyle = cColor.mediaBackdrop
			ctx.fillRect(x, y, w, h)
			ctx.font = cFont.placeholder
			ctx.fillStyle = cColor.subtitle
			const text = this.imageFailed ? "Preview unavailable" : "Loading preview…"
			ctx.fillText(text, x + (w - ctx.measureText(text).width) / 2, y + h / 2 + 4)
		}
		ctx.restore()

		if (media.badges && media.badges.length > 0) {
			drawBadgeRow(ctx, media.badges, x + 6, y + 6, w - 12, cColor.overlayBadge)
		}

		if (media.showCapturedAge && this.imageBitmap && this.imageBitmapUrl === media.url && this.imageCapturedMs != null) {
			const text = formatAgeCompact(this.imageCapturedMs)
			ctx.font = cFont.badge
			const bw = ctx.measureText(text).width + 12
			const bx = x + w - bw - 6
			const by = y + h - cBadgeHeight - 6
			ctx.fillStyle = cColor.overlayBadge
			ctx.beginPath()
			ctx.roundRect(bx, by, bw, cBadgeHeight, cBadgeHeight / 2)
			ctx.fill()
			ctx.fillStyle = cColor.value
			ctx.fillText(text, bx + 6, by + 11.5)
		}
	}

	private ensureImage(url: string): void {
		if (this.imageRequestedUrl === url) return
		this.imageRequestedUrl = url
		this.imageFailed = false

		fetch(url)
			.then(response => {
				if (!response.ok) throw new Error(`image fetch ${response.status}`)
				const uploadTimestamp = response.headers.get("x-bz-upload-timestamp")
				return response.blob().then(blob => ({ blob, uploadTimestamp }))
			})
			.then(({ blob, uploadTimestamp }) =>
				createImageBitmap(blob).then(bitmap => ({ bitmap, uploadTimestamp })))
			.then(({ bitmap, uploadTimestamp }) => {
				if (this.imageRequestedUrl !== url) {
					// Hover moved on while this loaded
					bitmap.close()
					return
				}
				this.imageBitmap?.close()
				this.imageBitmap = bitmap
				this.imageBitmapUrl = url
				const capturedMs = uploadTimestamp ? Number(uploadTimestamp) : NaN
				this.imageCapturedMs = Number.isFinite(capturedMs) && capturedMs > 0 ? capturedMs : null
				this.setNeedsUpdate()
			})
			.catch(() => {
				if (this.imageRequestedUrl === url) {
					this.imageFailed = true
					this.setNeedsUpdate()
				}
			})
	}
}

// ============================================================================
// Drawing helpers
// ============================================================================

/** Rows a field list occupies in the two-column grid */
function fieldRows(fields: HoverCardField[]): number {
	let rows = 0
	let col = 0
	for (const field of fields) {
		if (field.wide) {
			if (col === 1) rows++
			rows++
			col = 0
		} else if (col === 0) {
			col = 1
		} else {
			col = 0
			rows++
		}
	}
	return col === 1 ? rows + 1 : rows
}

function drawField(ctx: Ctx2D, field: HoverCardField, x: number, y: number, maxWidth: number): void {
	ctx.font = cFont.label
	ctx.fillStyle = field.labelColor ?? cColor.label
	const labelWidth = ctx.measureText(field.label).width
	ctx.fillText(field.label, x, y)
	const valueFont = field.mono ? cFont.valueMono : cFont.value
	ctx.font = valueFont
	ctx.fillStyle = cColor.value
	const valueX = x + labelWidth + 5
	ctx.fillText(clipText(ctx, field.value, maxWidth - labelWidth - 5, valueFont), valueX, y)
}

function drawBadgeRow(ctx: Ctx2D, badges: HoverCardBadge[], x: number, y: number, maxWidth: number, fill?: string): void {
	ctx.font = cFont.badge
	const spacing = supportsLetterSpacing(ctx)
	if (spacing) (ctx as CanvasRenderingContext2D).letterSpacing = "0.08em"
	let bx = x
	for (const badge of badges) {
		const text = badge.text.toUpperCase()
		const bw = ctx.measureText(text).width + 14
		if (bx + bw > x + maxWidth) break
		ctx.fillStyle = fill ?? withAlpha(badge.color, 0.18)
		ctx.beginPath()
		ctx.roundRect(bx, y, bw, cBadgeHeight, cBadgeHeight / 2)
		ctx.fill()
		ctx.fillStyle = badge.color
		ctx.fillText(text, bx + 7, y + 11.5)
		bx += bw + 5
	}
	if (spacing) (ctx as CanvasRenderingContext2D).letterSpacing = "0px"
}

function supportsLetterSpacing(ctx: Ctx2D): boolean {
	return "letterSpacing" in ctx
}

function clipText(ctx: Ctx2D, text: string, maxWidth: number, font: string): string {
	ctx.font = font
	if (ctx.measureText(text).width <= maxWidth) return text
	const ellipsis = "…"
	let low = 0
	let high = text.length
	let best = ellipsis
	while (low <= high) {
		const mid = Math.floor((low + high) / 2)
		const candidate = `${text.slice(0, mid).trimEnd()}${ellipsis}`
		if (ctx.measureText(candidate).width <= maxWidth) {
			best = candidate
			low = mid + 1
		} else {
			high = mid - 1
		}
	}
	return best
}

/** Greedy word wrap to at most `maxLines`, ellipsizing the last line */
function wrapText(ctx: Ctx2D, text: string, maxWidth: number, maxLines: number): string[] {
	const font = ctx.font
	const words = text.split(/\s+/).filter(Boolean)
	const lines: string[] = []
	let current = ""
	let i = 0
	for (; i < words.length; i++) {
		const word = words[i]
		const candidate = current ? `${current} ${word}` : word
		if (!current || ctx.measureText(candidate).width <= maxWidth) {
			current = candidate
			continue
		}
		lines.push(current)
		current = word
		if (lines.length === maxLines - 1) {
			i++
			break
		}
	}
	// The last line takes whatever is left and is clipped with an ellipsis
	const rest = [current, ...words.slice(i)].filter(Boolean).join(" ")
	if (rest) lines.push(rest)
	return lines.map((line, index) => index === lines.length - 1 ? clipText(ctx, line, maxWidth, font) : line)
}

/** CSS colour with a replaced alpha; accepts #rgb, #rrggbb and rgb()/rgba() */
export function withAlpha(color: string, alpha: number): string {
	const hex = color.trim()
	if (hex.startsWith("#")) {
		const raw = hex.length === 4
			? hex.slice(1).split("").map(c => c + c).join("")
			: hex.slice(1, 7)
		const r = parseInt(raw.slice(0, 2), 16)
		const g = parseInt(raw.slice(2, 4), 16)
		const b = parseInt(raw.slice(4, 6), 16)
		return `rgba(${r}, ${g}, ${b}, ${alpha})`
	}
	const match = /rgba?\(([^)]+)\)/.exec(hex)
	if (match) {
		const [r, g, b] = match[1].split(",").map(s => s.trim())
		return `rgba(${r}, ${g}, ${b}, ${alpha})`
	}
	return color
}

/** Single-unit recency ("55d ago" not "55d 13h ago") */
export function formatAgeCompact(time: string | number): string {
	const elapsed = Date.now() - new Date(time).getTime()
	const mins = Math.floor(elapsed / 60_000)
	if (mins < 1) return "just now"
	if (mins < 60) return `${mins}m ago`
	const hrs = Math.floor(mins / 60)
	if (hrs < 24) return `${hrs}h ago`
	const days = Math.floor(hrs / 24)
	return `${days}d ago`
}
